import { useEffect, useState, createContext, useContext, ReactNode, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import ConfirmDialog from "../../components/ConfirmDialog";
import { flushSync } from "react-dom";

interface UnsavedChangesContextType {
  setHasUnsavedChanges: (value: boolean) => void;
  promptUnsavedChanges: (onConfirm: () => void) => void;
}

interface UnsavedChangesProviderProps {
  children: ReactNode;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType | null>(null);

export const useUnsavedChangesContext = () => {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error(
      "useUnsavedChangesContext must be used within an UnsavedChangesProvider"
    );
  }
  return context;
};

export const useUnsavedChanges = (isDirty: boolean) => {
  const { setHasUnsavedChanges } = useUnsavedChangesContext();

  useEffect(() => {
    setHasUnsavedChanges(isDirty);
  }, [isDirty, setHasUnsavedChanges]);
};

export const useFlushUnsavedChanges = () => {
  const { setHasUnsavedChanges } = useUnsavedChangesContext();

  const flushUnsavedChanges = () => {
    flushSync(() => setHasUnsavedChanges(false));
  };

  return flushUnsavedChanges;
};

export const UnsavedChangesProvider = ({ children }: UnsavedChangesProviderProps) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [nextPath, setNextPath] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const isNavigatingRef = useRef(false);
  const history = useHistory();
  const location = useLocation();

  // Debug state changes
  useEffect(() => {
    console.log("State changed:", {
      hasUnsavedChanges,
      showDialog,
      nextPath,
      pendingAction,
      isNavigating: isNavigatingRef.current,
      currentPath: location.pathname,
    });
  }, [hasUnsavedChanges, showDialog, nextPath, pendingAction, location.pathname]);

  // Handle browser page unload with native warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      console.log("Before unload triggered:", {
        hasUnsavedChanges,
        isNavigating: isNavigatingRef.current,
      });
      if (hasUnsavedChanges && !isNavigatingRef.current) {
        e.preventDefault();
        e.returnValue = "You have unsaved changes. Do you want to continue?";
        return "You have unsaved changes. Do you want to continue?";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Intercept route changes
  useEffect(() => {
    let unblock: (() => void) | undefined;

    if (hasUnsavedChanges) {
      unblock = history.block((location) => {
        if (!isNavigatingRef.current) {
          isNavigatingRef.current = true;
          const next = location.pathname + location.search;
          setNextPath(next);
          setShowDialog(true);
          return false;
        }
        return undefined;
      });
    }

    return () => {
      if (unblock) unblock();
    };
  }, [hasUnsavedChanges, history]);

  // Intercept programmatic or local navigation triggers
  const promptUnsavedChanges = (onConfirm: () => void) => {
    if (hasUnsavedChanges) {
      isNavigatingRef.current = true;
      setPendingAction(() => onConfirm);
      setShowDialog(true);
    } else {
      onConfirm();
      isNavigatingRef.current = false;
    }
  };

  const handleDialogClose = (confirmed: boolean) => {
    console.log("Dialog closing:", {
      confirmed,
      nextPath,
      pendingAction,
      isNavigating: isNavigatingRef.current,
    });
    setShowDialog(false);

    if (confirmed) {
      setHasUnsavedChanges(false);

      if (pendingAction) {
        pendingAction();
      } else if (nextPath) {
        history.push(nextPath);
      }
    }

    setNextPath(null);
    setPendingAction(null);
    isNavigatingRef.current = false;
  };

  return (
    <UnsavedChangesContext.Provider
      value={{
        setHasUnsavedChanges,
        promptUnsavedChanges,
      }}
    >
      {children}
      <ConfirmDialog
        title="Unsaved Changes"
        message="You have unsaved changes. Do you want to continue?"
        isOpen={showDialog}
        handleClose={handleDialogClose}
      />
    </UnsavedChangesContext.Provider>
  );
};
